import React, { createContext, useCallback, useMemo, useRef, useState } from "react";
import { getZIndex, Z_INDEX } from "../../core/utils/zIndexScale";

export const LEGACY_OVERLAY_Z_INDEX = 200;

const evaluateEnvFlag = () => {
  const raw = (process.env.REACT_APP_ENABLE_ADD_BUTTON_LAYERING || "").trim().toLowerCase();
  if (!raw) return true;
  return raw === "1" || raw === "true" || raw === "yes" || raw === "enabled";
};

const defaultFeatureFlags = {
  enableAddButtonLayering: evaluateEnvFlag(),
};

const createToken = (defaults = {}) =>
  Object.freeze({
    wrapperZ: defaults.wrapperZ ?? getZIndex("EDIT_OVERLAY", 2),
    buttonBaseZ: defaults.buttonBaseZ ?? getZIndex("ADD_BUTTON_BASE", 2),
    buttonHoverZ: defaults.buttonHoverZ ?? getZIndex("ADD_BUTTON_HOVER", 2),
    indicatorZ: defaults.indicatorZ ?? Z_INDEX.EDIT_INDICATOR,
    orientation: defaults.orientation ?? "horizontal",
    pointerPadding: defaults.pointerPadding ?? 24,
    variant: defaults.variant ?? "component",
    placement: defaults.placement ?? "before",
    legacyWrapperZ: defaults.legacyWrapperZ ?? LEGACY_OVERLAY_Z_INDEX,
  });

const pageToken = (overrides = {}) =>
  createToken({
    wrapperZ: getZIndex("EDIT_OVERLAY", 8),
    buttonBaseZ: getZIndex("ADD_BUTTON_BASE", 6),
    buttonHoverZ: getZIndex("ADD_BUTTON_HOVER", 6),
    indicatorZ: Z_INDEX.EDIT_INDICATOR + 1,
    pointerPadding: 32,
    variant: "page",
    ...overrides,
  });

const componentToken = (overrides = {}) =>
  createToken({
    wrapperZ: getZIndex("EDIT_OVERLAY", 2),
    buttonBaseZ: getZIndex("ADD_BUTTON_BASE", 2),
    buttonHoverZ: getZIndex("ADD_BUTTON_HOVER", 2),
    variant: "component",
    ...overrides,
  });

const heroToken = (overrides = {}) =>
  createToken({
    wrapperZ: getZIndex("EDIT_OVERLAY", 4),
    buttonBaseZ: getZIndex("ADD_BUTTON_BASE", 3),
    buttonHoverZ: getZIndex("ADD_BUTTON_HOVER", 3),
    variant: "component",
    ...overrides,
  });

const buttonCollectionToken = (overrides = {}) =>
  createToken({
    wrapperZ: getZIndex("EDIT_OVERLAY", 3),
    buttonBaseZ: getZIndex("ADD_BUTTON_BASE", 3),
    buttonHoverZ: getZIndex("ADD_BUTTON_HOVER", 3),
    pointerPadding: 20,
    variant: "component",
    ...overrides,
  });

export const ADD_BUTTON_LAYER_TOKENS = {
  "page-row-before": pageToken({ placement: "before" }),
  "page-row-after": pageToken({ placement: "after" }),
  "page-linear-before": pageToken({ placement: "before" }),
  "page-linear-after": pageToken({ placement: "after" }),
  "page-empty": pageToken({ placement: "cover" }),
  "component-empty": componentToken({ placement: "cover" }),
  "component-row-before": componentToken({ placement: "before" }),
  "component-row-after": componentToken({ placement: "after" }),
  "component-linear-before": componentToken({ placement: "before" }),
  "component-linear-after": componentToken({ placement: "after" }),
  "hero-item-before": heroToken({ placement: "before" }),
  "hero-item-after": heroToken({ placement: "after" }),
  "hero-grid-after": heroToken({ placement: "after" }),
  "button-collection-before": buttonCollectionToken({ placement: "before" }),
  "button-collection-after": buttonCollectionToken({ placement: "after" }),
  "button-collection-empty": buttonCollectionToken({ placement: "cover" }),
  default: componentToken(),
};

export const AddButtonLayerContext = createContext({
  tokens: ADD_BUTTON_LAYER_TOKENS,
  activeOverlayId: null,
  registerOverlay: () => {},
  unregisterOverlay: () => {},
  activateOverlay: () => {},
  deactivateOverlay: () => {},
  featureFlags: defaultFeatureFlags,
});

export const AddButtonLayerProvider = ({
  children,
  tokens = ADD_BUTTON_LAYER_TOKENS,
  featureFlags,
  analytics,
}) => {
  const overlaysRef = useRef(new Map());
  const analyticsRef = useRef(analytics || {});
  analyticsRef.current = analytics || {};
  const [activeOverlayId, setActiveOverlayId] = useState(null);

  const registerOverlay = useCallback((overlayId, meta = {}) => {
    overlaysRef.current.set(overlayId, meta);
  }, []);

  const unregisterOverlay = useCallback((overlayId) => {
    overlaysRef.current.delete(overlayId);
    setActiveOverlayId((prev) => (prev === overlayId ? null : prev));
  }, []);

  const activateOverlay = useCallback((overlayId, meta = {}) => {
    setActiveOverlayId((prev) => {
      if (prev === overlayId) return prev;
      const { onDeactivate, onActivate } = analyticsRef.current;
      if (prev && typeof onDeactivate === "function") {
        onDeactivate(prev, overlaysRef.current.get(prev) || null);
      }
      if (typeof onActivate === "function") {
        onActivate(overlayId, meta);
      }
      return overlayId;
    });
  }, []);

  const deactivateOverlay = useCallback((overlayId) => {
    setActiveOverlayId((prev) => {
      if (prev !== overlayId) return prev;
      const { onDeactivate } = analyticsRef.current;
      if (typeof onDeactivate === "function") {
        onDeactivate(overlayId, overlaysRef.current.get(overlayId) || null);
      }
      return null;
    });
  }, []);

  const mergedFeatureFlags = useMemo(
    () => ({
      enableAddButtonLayering:
        featureFlags?.enableAddButtonLayering ?? defaultFeatureFlags.enableAddButtonLayering,
    }),
    [featureFlags],
  );

  const contextValue = useMemo(
    () => ({
      tokens,
      activeOverlayId,
      registerOverlay,
      unregisterOverlay,
      activateOverlay,
      deactivateOverlay,
      featureFlags: mergedFeatureFlags,
    }),
    [
      tokens,
      activeOverlayId,
      registerOverlay,
      unregisterOverlay,
      activateOverlay,
      deactivateOverlay,
      mergedFeatureFlags,
    ],
  );

  return (
    <AddButtonLayerContext.Provider value={contextValue}>
      {children}
    </AddButtonLayerContext.Provider>
  );
};




